import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle,
  Copy,
  Eye,
  Key,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import {
  createAccountRequest,
  deleteAccountRequest,
  fetchAccounts,
  resetAccountPasswordRequest,
  updateAccountRequest,
} from '../../api/client';
import { AccountStatusBadge } from '../../components/account/AccountStatusBadge';
import { StatCard } from '../../components/dashboard/StatCard';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { Card, CardContent } from '../../components/ui/Card';
import { Modal, ModalActionButton } from '../../components/ui/Modal';
import { APP_NAV, useAppNavigation } from '../../context/AppNavigationContext';
import { useTenantView } from '../../context/TenantViewContext';
import { cn } from '../../lib/utils';

function CredentialsBanner({ title, email, password, onDismiss }) {
  const [copied, setCopied] = useState(null);

  async function copyValue(kind, value) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      // ignore clipboard failures
    }
  }

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <CheckCircle size={16} weight="fill" aria-hidden="true" />
          {title}
        </div>
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs font-semibold text-emerald-800 underline"
          >
            Dismiss
          </button>
        ) : null}
      </div>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span>
            Email: <strong className="break-all">{email}</strong>
          </span>
          <button
            type="button"
            onClick={() => copyValue('email', email)}
            className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200 transition hover:scale-105"
          >
            <Copy size={12} weight="bold" aria-hidden="true" />
            {copied === 'email' ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span>
            Password: <strong className="break-all">{password}</strong>
          </span>
          <button
            type="button"
            onClick={() => copyValue('password', password)}
            className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200 transition hover:scale-105"
          >
            <Copy size={12} weight="bold" aria-hidden="true" />
            {copied === 'password' ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, children, className }) {
  return (
    <div className={cn('min-w-0', className)}>
      <label
        htmlFor={id}
        className="mb-1.5 block font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-[var(--brand-primary,#1A428A)] focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15 disabled:opacity-60';

const EMPTY_CREATE = {
  name: '',
  email: '',
  displayName: '',
  seedDemoData: true,
};

export function AccountsPage() {
  const { setActiveNav } = useAppNavigation();
  const { enterTenantView, viewingTenant } = useTenantView();
  const [searchQuery, setSearchQuery] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [resetCredentials, setResetCredentials] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE);

  const [editingAccount, setEditingAccount] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    displayName: '',
    isActive: true,
  });

  const [confirmAction, setConfirmAction] = useState(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAccounts();
      setAccounts(data);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return accounts;
    return accounts.filter((account) => {
      const business = String(account.name || '').toLowerCase();
      const email = String(account.userEmail || '').toLowerCase();
      return business.includes(query) || email.includes(query);
    });
  }, [accounts, searchQuery]);

  const stats = useMemo(() => {
    const total = accounts.length;
    const active = accounts.filter((account) => account.isActive).length;
    const pending = accounts.filter((account) => !account.onboardingComplete).length;
    return { total, active, pending };
  }, [accounts]);

  function openCreate() {
    setForm(EMPTY_CREATE);
    setError(null);
    setCreateOpen(true);
  }

  function closeCreate() {
    if (submitting) return;
    setCreateOpen(false);
  }

  function startEdit(account) {
    setEditingAccount(account);
    setEditForm({
      name: account.name,
      email: account.userEmail,
      displayName: account.userDisplayName,
      isActive: account.isActive,
    });
    setError(null);
  }

  function closeEdit() {
    if (submitting) return;
    setEditingAccount(null);
  }

  function handleViewData(account) {
    enterTenantView(account);
    setActiveNav(APP_NAV.OVERVIEW);
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await createAccountRequest(form);
      setCreatedCredentials({
        email: result.account.userEmail,
        password: result.generatedPassword,
        seededInvoiceCount: result.seededInvoiceCount,
      });
      setResetCredentials(null);
      setForm(EMPTY_CREATE);
      setCreateOpen(false);
      await loadAccounts();
    } catch (submitError) {
      setError(submitError.message || 'Unable to create account.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEditSubmit(event) {
    event.preventDefault();
    if (!editingAccount) return;
    setError(null);
    setSubmitting(true);
    try {
      await updateAccountRequest(editingAccount.id, {
        name: editForm.name,
        email: editForm.email,
        displayName: editForm.displayName,
        isActive: editForm.isActive,
      });
      setEditingAccount(null);
      await loadAccounts();
    } catch (submitError) {
      setError(submitError.message || 'Unable to update account.');
    } finally {
      setSubmitting(false);
    }
  }

  async function runConfirmAction() {
    if (!confirmAction) return;
    const { type, account } = confirmAction;
    setError(null);
    setSubmitting(true);
    try {
      if (type === 'delete') {
        await deleteAccountRequest(account.id);
        if (editingAccount?.id === account.id) setEditingAccount(null);
        await loadAccounts();
      } else if (type === 'reset') {
        const result = await resetAccountPasswordRequest(account.id);
        setResetCredentials({
          email: account.userEmail,
          password: result.generatedPassword,
        });
        setCreatedCredentials(null);
      }
      setConfirmAction(null);
    } catch (submitError) {
      setError(
        submitError.message ||
          (type === 'delete' ? 'Unable to delete account.' : 'Unable to reset password.')
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && accounts.length === 0) {
    return <LoadingOverlay loading />;
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-5">
      <div className="grid min-w-0 gap-4 sm:grid-cols-3">
        <StatCard
          title="Tenants"
          value={String(stats.total)}
          change="—"
          trend="neutral"
          context={stats.total ? 'Customer accounts' : 'None provisioned yet'}
          featured
        />
        <StatCard
          title="Active"
          value={String(stats.active)}
          change="—"
          trend="neutral"
          context={stats.active ? 'Can sign in' : 'No active tenants'}
        />
        <StatCard
          title="Onboarding pending"
          value={String(stats.pending)}
          change="—"
          trend="neutral"
          context={stats.pending ? 'Still setting up' : 'All complete'}
        />
      </div>

      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}

      {createdCredentials ? (
        <div className="space-y-2">
          <CredentialsBanner
            title="Account created — share these credentials once"
            email={createdCredentials.email}
            password={createdCredentials.password}
            onDismiss={() => setCreatedCredentials(null)}
          />
          {createdCredentials.seededInvoiceCount > 0 ? (
            <p className="text-xs text-emerald-800">
              Seeded {createdCredentials.seededInvoiceCount} demo invoices.
            </p>
          ) : null}
        </div>
      ) : null}

      {resetCredentials ? (
        <CredentialsBanner
          title="New password generated — share with the client once"
          email={resetCredentials.email}
          password={resetCredentials.password}
          onDismiss={() => setResetCredentials(null)}
        />
      ) : null}

      <Card className="min-w-0">
        <CardContent className="space-y-4 px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-sm font-bold text-[#201C35]">Customer tenants</h2>
              <p className="mt-0.5 text-xs text-slate-600">
                {searchQuery.trim()
                  ? `${filteredAccounts.length} of ${accounts.length} shown`
                  : `${accounts.length} total`}
              </p>
            </div>
            <ModalActionButton onClick={openCreate}>
              <Plus size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
              Create tenant
            </ModalActionButton>
          </div>

          {accounts.length > 0 ? (
            <div className="relative max-w-md">
              <MagnifyingGlass
                size={16}
                weight="bold"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by business or email…"
                className="h-10 w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-9 text-sm text-slate-800 outline-none transition focus:border-[var(--brand-primary,#1A428A)] focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15"
                aria-label="Search customer accounts"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X size={14} weight="bold" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : null}

          {accounts.length === 0 ? (
            <div className="rounded-xl border border-[#C9A227]/35 bg-[#F8F1E3] px-4 py-4">
              <p className="text-sm font-semibold text-[#5D2E17]">No tenants yet</p>
              <p className="mt-1 text-[12px] leading-5 text-[#5D2E17]/80">
                Create a customer account to provision login access and optional demo data.
              </p>
              <div className="mt-3">
                <ModalActionButton onClick={openCreate}>
                  <Plus size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
                  Create tenant
                </ModalActionButton>
              </div>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <p className="text-sm text-slate-600">
              No accounts match <strong>{searchQuery.trim()}</strong>. Try a different business name
              or email.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Business</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Onboarding</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewData(account)}
                            disabled={submitting || !account.isActive}
                            className="font-display text-sm font-bold text-[#201C35] underline-offset-2 hover:text-[var(--brand-primary,#1A428A)] hover:underline disabled:opacity-50"
                          >
                            {account.name}
                          </button>
                          {viewingTenant?.id === account.id ? (
                            <AccountStatusBadge status="viewing" />
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{account.userEmail}</td>
                      <td className="px-3 py-3">
                        <AccountStatusBadge
                          status={account.onboardingComplete ? 'complete' : 'pending'}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <AccountStatusBadge status={account.isActive ? 'active' : 'inactive'} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            title="View customer data"
                            onClick={() => handleViewData(account)}
                            disabled={submitting || !account.isActive}
                            className="rounded-full p-2 text-slate-500 transition hover:scale-105 hover:bg-slate-100 hover:text-[var(--brand-primary,#1A428A)] disabled:opacity-50"
                          >
                            <Eye size={16} weight="bold" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            title="Edit account"
                            onClick={() => startEdit(account)}
                            disabled={submitting}
                            className="rounded-full p-2 text-slate-500 transition hover:scale-105 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
                          >
                            <PencilSimple size={16} weight="bold" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            title="Reset password"
                            onClick={() => setConfirmAction({ type: 'reset', account })}
                            disabled={submitting}
                            className="rounded-full p-2 text-slate-500 transition hover:scale-105 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
                          >
                            <Key size={16} weight="bold" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            title="Delete account"
                            onClick={() => setConfirmAction({ type: 'delete', account })}
                            disabled={submitting}
                            className="rounded-full p-2 text-rose-500 transition hover:scale-105 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                          >
                            <Trash size={16} weight="bold" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create tenant */}
      <Modal
        open={createOpen}
        onClose={closeCreate}
        title="Create tenant"
        description="A password will be generated automatically for the client to sign in."
        footer={
          <>
            <ModalActionButton variant="outline" onClick={closeCreate} disabled={submitting}>
              Cancel
            </ModalActionButton>
            <ModalActionButton type="submit" form="create-tenant-form" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create account'}
            </ModalActionButton>
          </>
        }
      >
        <form id="create-tenant-form" className="grid gap-4 sm:grid-cols-2" onSubmit={handleCreateSubmit}>
          <Field id="create-name" label="Business name">
            <input
              id="create-name"
              type="text"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              className={inputClass}
              required
              disabled={submitting}
            />
          </Field>
          <Field id="create-email" label="Login email">
            <input
              id="create-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              className={inputClass}
              required
              disabled={submitting}
            />
          </Field>
          <Field id="create-display" label="Display name (optional)" className="sm:col-span-2">
            <input
              id="create-display"
              type="text"
              value={form.displayName}
              onChange={(event) =>
                setForm((current) => ({ ...current, displayName: event.target.value }))
              }
              className={inputClass}
              disabled={submitting}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.seedDemoData}
              onChange={(event) =>
                setForm((current) => ({ ...current, seedDemoData: event.target.checked }))
              }
              disabled={submitting}
              className="rounded border-slate-300"
            />
            Include demo data (lite seed for development)
          </label>
        </form>
      </Modal>

      {/* Edit tenant */}
      <Modal
        open={Boolean(editingAccount)}
        onClose={closeEdit}
        title={editingAccount ? `Edit ${editingAccount.name}` : 'Edit tenant'}
        description="Update login details and active status for this customer."
        footer={
          <>
            <ModalActionButton variant="outline" onClick={closeEdit} disabled={submitting}>
              Cancel
            </ModalActionButton>
            <ModalActionButton type="submit" form="edit-tenant-form" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save changes'}
            </ModalActionButton>
          </>
        }
      >
        <form id="edit-tenant-form" className="grid gap-4 sm:grid-cols-2" onSubmit={handleEditSubmit}>
          <Field id="edit-name" label="Business name">
            <input
              id="edit-name"
              type="text"
              value={editForm.name}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, name: event.target.value }))
              }
              className={inputClass}
              required
              disabled={submitting}
            />
          </Field>
          <Field id="edit-email" label="Login email">
            <input
              id="edit-email"
              type="email"
              value={editForm.email}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, email: event.target.value }))
              }
              className={inputClass}
              required
              disabled={submitting}
            />
          </Field>
          <Field id="edit-display" label="Display name" className="sm:col-span-2">
            <input
              id="edit-display"
              type="text"
              value={editForm.displayName}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, displayName: event.target.value }))
              }
              className={inputClass}
              required
              disabled={submitting}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={editForm.isActive}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, isActive: event.target.checked }))
              }
              disabled={submitting}
              className="rounded border-slate-300"
            />
            Account is active (client can sign in)
          </label>
        </form>
      </Modal>

      {/* Confirm delete / reset */}
      <Modal
        open={Boolean(confirmAction)}
        onClose={() => {
          if (!submitting) setConfirmAction(null);
        }}
        size="sm"
        title={
          confirmAction?.type === 'delete'
            ? 'Delete tenant?'
            : confirmAction?.type === 'reset'
              ? 'Reset password?'
              : 'Confirm'
        }
        description={
          confirmAction?.type === 'delete'
            ? `This removes "${confirmAction.account.name}", its login user, and all revenue data. This cannot be undone.`
            : confirmAction?.type === 'reset'
              ? `Generate a new password for ${confirmAction.account.userEmail}? The current password will stop working immediately.`
              : undefined
        }
        footer={
          <>
            <ModalActionButton
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={submitting}
            >
              Cancel
            </ModalActionButton>
            <ModalActionButton
              variant={confirmAction?.type === 'delete' ? 'danger' : 'primary'}
              onClick={runConfirmAction}
              disabled={submitting}
            >
              {submitting
                ? 'Working…'
                : confirmAction?.type === 'delete'
                  ? 'Delete tenant'
                  : 'Reset password'}
            </ModalActionButton>
          </>
        }
      >
        <p className="text-xs leading-5 text-slate-600">
          {confirmAction?.type === 'delete'
            ? 'Only continue if you are sure this tenant should be removed from the platform.'
            : 'Share the new password with the client once, then ask them to change it after sign-in.'}
        </p>
      </Modal>
    </div>
  );
}
